/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Entity } from '@backstage/catalog-model';
import type { FilterPredicate } from '@backstage/filter-predicates';
import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type { JSX, ReactElement } from 'react';

import {
  createExtensionBlueprint,
  createExtensionDataRef,
  ExtensionBoundary,
} from '@backstage/frontend-plugin-api';
import { z } from 'zod';

import {
  entityFilterExpressionDataRef,
  entityFilterFunctionDataRef,
  entityLayoutOrderRef,
} from './extensionData';
import { resolveEntityFilterData } from './resolveEntityFilterData';

/** @alpha */
export type EntityLayoutBlueprintProps = {
  groupDefinitions: EntityContentGroupDefinitions;
  header?: JSX.Element;
  routes: readonly {
    children: JSX.Element;
    group?: string;
    icon?: ReactElement | string;
    path: string;
    title: string;
  }[];
  showNavItemIcons: boolean;
};

const entityLayoutComponentDataRef = createExtensionDataRef<
  (props: EntityLayoutBlueprintProps) => JSX.Element
>().with({
  id: 'catalog.entity-layout.component',
});

/**
 * @alpha
 * @deprecated TODO this file can be removed after the following PR is released:
 *              1. https://github.com/backstage/backstage/pull/31128
 **/
export const EntityLayoutBlueprint = createExtensionBlueprint({
  attachTo: { id: 'page:catalog/entity', input: 'layouts' },
  configSchema: {
    filter: z.any().optional(),
  },
  dataRefs: {
    component: entityLayoutComponentDataRef,
    filterFunction: entityFilterFunctionDataRef,
    order: entityLayoutOrderRef,
  },
  * factory(
    params: {
      filter?: ((entity: Entity) => boolean) | FilterPredicate;
      loader: () => Promise<(props: EntityLayoutBlueprintProps) => JSX.Element>;
      order?: number;
    },
    { config, node },
  ) {
    const { filter, loader, order } = params;

    yield* resolveEntityFilterData(filter, config, node);

    if (loader) {
      yield entityLayoutComponentDataRef(
        ExtensionBoundary.lazyComponent(node, loader),
      );
    }

    if (order !== undefined) {
      yield entityLayoutOrderRef(order);
    }
  },
  kind: 'entity-layout',
  output: [
    entityFilterFunctionDataRef.optional(),
    entityFilterExpressionDataRef.optional(),
    entityLayoutComponentDataRef.optional(),
    entityLayoutOrderRef.optional(),
  ],
});
